import type { Container } from "../types/booking";

export type LinkedMasterSummary = {
  id: number;
  reference: string | null;
  transport_type: number | null;
  agent_id: number | null;
  created_at: string | null;
};

export function getLinkedMastersFromContainer(
  container: Pick<Container, "master_container_info"> | {
    master_container_info?: Container["master_container_info"];
  }
): LinkedMasterSummary[] {
  const mcc = container.master_container_info;
  const rows = Array.isArray(mcc) ? mcc : mcc ? [mcc] : [];
  const masters: LinkedMasterSummary[] = [];
  for (const row of rows) {
    const mc = row?.master_containers;
    const single = Array.isArray(mc) ? mc[0] : mc;
    if (single?.id) {
      masters.push({
        id: single.id,
        reference: single.reference ?? null,
        transport_type: single.transport_type ?? null,
        agent_id: single.agent_id ?? null,
        created_at: single.created_at ?? null,
      });
    }
  }
  return masters;
}

/** Primary master for display/filter (first linked). */
export function getPrimaryMaster(
  container: Parameters<typeof getLinkedMastersFromContainer>[0]
): LinkedMasterSummary | null {
  return getLinkedMastersFromContainer(container)[0] ?? null;
}

export function getContainersFromBooking(booking: {
  container_info?: Container | Container[] | null;
}): Container[] {
  if (!booking.container_info) return [];
  return Array.isArray(booking.container_info)
    ? booking.container_info
    : [booking.container_info];
}

/** Master BL/AWB reference from linked master(s) on booking containers. */
export function getMasterAwbReferenceFromBooking(booking: {
  container_info?: Container | Container[] | null;
}): string | null {
  for (const container of getContainersFromBooking(booking)) {
    const ref = getPrimaryMaster(container)?.reference?.trim();
    if (ref) return ref;
  }
  return null;
}

/** First non-empty container reference on the booking (e.g. carrier ref / AWB-style ref). */
export function getContainerReferenceFromBooking(booking: {
  container_info?: Container | Container[] | null;
}): string | null {
  for (const container of getContainersFromBooking(booking)) {
    const ref = container.reference?.trim();
    if (ref) return ref;
  }
  return null;
}

/**
 * Default Lean Customs AWB/B/L document number: linked master reference first,
 * then connected container reference when no master ref is available.
 */
export function getDefaultLeanCustomsDocumentNumberFromBooking(booking: {
  container_info?: Container | Container[] | null;
}): string | null {
  return (
    getMasterAwbReferenceFromBooking(booking) ??
    getContainerReferenceFromBooking(booking)
  );
}
